import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

import { useLazyDownloadReceiveAttachmentQuery } from '../api/receiveConfirmApi';
import type { ReceiveAttachmentDto } from './attachment-types';

/**
 * Downloads a receive attachment by its own `shipmentId`.
 *
 * That id is the point of the hook: after a partial receive the file hangs off
 * the receipt, not off the shipment being received, so taking the id from the
 * attachment rather than from the screen is what keeps the request from
 * resolving to a `404`. The response handler writes the file to disk, so there
 * is nothing to do with the result but report a failure.
 */
export const useReceiveAttachmentDownload = () => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [downloadAttachment, { isFetching }] = useLazyDownloadReceiveAttachmentQuery();

  const download = useCallback(
    async (attachment: ReceiveAttachmentDto) => {
      try {
        await downloadAttachment({
          shipmentId: attachment.shipmentId,
          attachmentId: attachment.id,
          projectId,
          siteId
        }).unwrap();
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            t('receive.attachments.error.download', 'Could not download {{file}}', { file: attachment.fileName })
          )
        );
      }
    },
    [downloadAttachment, projectId, siteId, t]
  );

  return { download, isDownloading: isFetching };
};
